/**
 * acomo CLI エントリポイント
 */

import { readFileSync } from "fs";
import { resolve, dirname } from "path";
import { fileURLToPath } from "url";
import yargs from "yargs";

/** 実行中スクリプトのディレクトリ（ESM 前提） */
const scriptDir = dirname(fileURLToPath(import.meta.url));
import { hideBin } from "yargs/helpers";
import { ResponseError } from "@acomo/client";
import {
  createConfiguration,
  buildOperationRegistry,
} from "./lib/api-client.js";
import { resolveConfig } from "./lib/config-store.js";
import * as loginCommand from "./commands/login.js";
import * as logoutCommand from "./commands/logout.js";
import * as configCommand from "./commands/config.js";
import * as schemaCommand from "./commands/schema.js";
import { operationDescriptions } from "./generated/operation-descriptions.js";
import { operationParams } from "./generated/operation-params.js";
import type {
  OperationParamInfo,
  ParamInfo,
  BodyPropertyInfo,
} from "./generated/operation-params.js";
import {
  buildRootHelp,
  buildCommandHelp,
  type HelpDescriptor,
  type HelpOption,
} from "./lib/help-schema.js";

/** 共通 help 用のグローバルオプション（全コマンドに付与） */
const GLOBAL_OPTIONS: HelpOption[] = [
  { name: "help", type: "boolean", description: "Show help" },
  { name: "version", type: "boolean", description: "Show version number" },
];

/** --help 検出時に argv からコマンド名を抜き出すため、値を取るオプションを列挙 */
const OPTIONS_WITH_VALUE = new Set([
  "--base-url",
  "--tenant-id",
  "--access-token",
]);

/**
 * process.argv から --help 要求とコマンド名を取得する。
 * @returns { help: true, command: string | null } help が要求されていれば command は第一位置引数（なければ root）
 */
function getHelpRequestedCommand(argv: string[]): {
  help: boolean;
  command: string | null;
} {
  const args = argv.slice(2); // drop node and script path
  let helpRequested = false;
  let command: string | null = null;
  let i = 0;
  while (i < args.length) {
    const a = args[i];
    if (a === "--help" || a === "-h") {
      helpRequested = true;
      i += 1;
      continue;
    }
    if (a.startsWith("-")) {
      if (OPTIONS_WITH_VALUE.has(a)) i += 1; // skip option value
      i += 1;
      continue;
    }
    if (command === null) command = a;
    i += 1;
  }
  return { help: helpRequested, command: helpRequested ? command : null };
}

/**
 * オペレーション ID からリクエストスキーマを取得する（正の情報源は operationParams のみ）。
 * --help と将来の schema コマンドの両方でこの取得経路を使う。
 */
function getOperationRequestSchema(
  operationId: string,
): OperationParamInfo | undefined {
  return operationParams[operationId];
}

/** 終了コード */
const EXIT_CODES = {
  GENERAL: 1,
  AUTH: 2,
  VALIDATION: 3,
  NOT_FOUND: 4,
} as const;

/**
 * 位置引数からリクエストボディ JSON を解決
 */
async function resolveBody(
  positional?: string,
): Promise<Record<string, unknown> | undefined> {
  if (positional) {
    try {
      return JSON.parse(positional);
    } catch (error) {
      throw new Error(`無効な JSON です: ${positional}`);
    }
  }
  return undefined;
}

/**
 * named options（path/query params）とボディから SDK クライアントが期待する形式に変換する。
 * requestBody がある場合は body を dtoName キー配下にラップする。
 */
function buildClientParams(
  operationId: string,
  namedParams: Record<string, unknown>,
  body: Record<string, unknown> | undefined,
): Record<string, unknown> {
  const paramInfo = getOperationRequestSchema(operationId);
  if (!paramInfo?.requestBody) return namedParams;
  const { dtoName } = paramInfo.requestBody;
  return { ...namedParams, [dtoName]: body ?? {} };
}

/** 型文字列から使用例用のプレースホルダ値を返す */
function placeholderForType(type: string): unknown {
  if (type === "string") return "";
  if (type === "number" || type === "integer") return 0;
  if (type === "boolean") return false;
  if (type.includes("[]")) return [];
  if (type === "object" || /[A-Z]/.test(type) || type.endsWith("Dto"))
    return {};
  return "";
}

/** パラメータ配列から必須を満たす最小の例用オブジェクトを生成（path/query 用） */
function buildExampleParams(params: ParamInfo[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const p of params) {
    if (p.required) out[p.name] = placeholderForType(p.type);
  }
  return out;
}

/** requestBody の必須プロパティから例用オブジェクトを生成 */
function buildExampleBody(
  properties: BodyPropertyInfo[],
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const p of properties) {
    if (p.required) out[p.name] = placeholderForType(p.type);
  }
  return out;
}

/** 説明を正規化（<br> を改行に）。JSON ヘルプでは省略せず全文を保持する。 */
function normalizeDescription(desc: string): string {
  return desc.replace(/<br\s*\/?>/gi, "\n").trim();
}

/**
 * root help（acomo --help）用の descriptor を組み立てる。
 * 組み立て時に registry があれば api-command 一覧に含める。
 */
function buildRootHelpDescriptor(): HelpDescriptor {
  const commands: Array<{
    name: string;
    kind: "builtin-command" | "api-command";
    summary: string;
  }> = [
    {
      name: loginCommand.command(),
      kind: "builtin-command",
      summary: loginCommand.describe(),
    },
    {
      name: logoutCommand.command(),
      kind: "builtin-command",
      summary: logoutCommand.describe(),
    },
    {
      name: "config",
      kind: "builtin-command",
      summary: configCommand.describe(),
    },
    {
      name: "schema",
      kind: "builtin-command",
      summary: schemaCommand.describe(),
    },
  ];
  const config = resolveConfig();
  if (config) {
    try {
      const configuration = createConfiguration();
      const registry = buildOperationRegistry(configuration);
      for (const [operationId] of registry) {
        const summary = operationDescriptions[operationId] ?? operationId;
        commands.push({ name: operationId, kind: "api-command", summary });
      }
    } catch {
      // 認証未完了時は api コマンドは出さない
    }
  }
  return buildRootHelp(commands);
}

/**
 * 手書きコマンド（login / logout / config）の help descriptor を組み立てる。
 */
function buildBuiltinHelpDescriptor(cmd: string): HelpDescriptor | null {
  const scriptName = "acomo";
  let summary: string;
  let positionals: Array<{
    name: string;
    type: string;
    required: boolean;
    description: string;
  }> = [];
  let options: HelpOption[];
  let exampleCommand: string | null = null;
  if (cmd === loginCommand.command()) {
    summary = loginCommand.describe();
    options = [...loginCommand.helpOptions, ...GLOBAL_OPTIONS];
    exampleCommand =
      "acomo login --tenant-id <tenantId> --access-token <accessToken>";
  } else if (cmd === logoutCommand.command()) {
    summary = logoutCommand.describe();
    options = [...logoutCommand.helpOptions, ...GLOBAL_OPTIONS];
    exampleCommand = "acomo logout";
  } else if (cmd === "config") {
    summary = configCommand.describe();
    positionals = [...configCommand.helpPositionals];
    options = [...configCommand.helpOptions, ...GLOBAL_OPTIONS];
    exampleCommand = "acomo config show";
  } else if (cmd === "schema") {
    summary = schemaCommand.describe();
    positionals = [...schemaCommand.helpPositionals];
    options = [...schemaCommand.helpOptions, ...GLOBAL_OPTIONS];
    exampleCommand = "acomo schema show";
  } else {
    return null;
  }
  return buildCommandHelp({
    command: cmd,
    kind: "builtin-command",
    summary,
    usage: `${scriptName} ${cmd}${positionals.length ? " <subcommand> [key] [value]" : " [options]"}`,
    positionals,
    options,
    inputSchema: null,
    minimalArgs: null,
    exampleCommand,
  });
}

/**
 * OpenAPI 由来コマンドの help descriptor を組み立てる（共通スキーマの api-command 形）。
 */
function buildApiCommandHelpDescriptor(
  operationId: string,
): HelpDescriptor | null {
  const paramInfo = getOperationRequestSchema(operationId);
  if (!paramInfo) return null;
  const summary = operationDescriptions[operationId] ?? operationId;

  const toParamEntry = (p: ParamInfo) => {
    const entry: {
      name: string;
      type: string;
      required: boolean;
      description: string;
      example?: unknown;
    } = {
      name: p.name,
      type: p.type,
      required: p.required,
      description: normalizeDescription(p.description || ""),
    };
    if (p.example !== undefined) entry.example = p.example;
    return entry;
  };
  const pathParams = paramInfo.params
    .filter((p) => p.in === "path")
    .map(toParamEntry);
  const queryParams = paramInfo.params
    .filter((p) => p.in === "query")
    .map(toParamEntry);
  const requestBody = paramInfo.requestBody
    ? {
        properties: paramInfo.requestBody.properties.map((p) => {
          const entry: {
            name: string;
            type: string;
            required: boolean;
            description: string;
            example?: unknown;
          } = {
            name: p.name,
            type: p.type,
            required: p.required,
            description: normalizeDescription(p.description || ""),
          };
          if (p.example !== undefined) entry.example = p.example;
          return entry;
        }),
      }
    : null;

  // path/query params を named options として登録
  const paramOptions: HelpOption[] = paramInfo.params.map((p) => ({
    name: p.name,
    type:
      p.type === "number" || p.type === "integer"
        ? "number"
        : p.type === "boolean"
          ? "boolean"
          : "string",
    description: normalizeDescription(p.description || ""),
    required: p.required,
  }));

  // body のみを minimalArgs として生成
  const minimalArgs: Record<string, unknown> | null = paramInfo.requestBody
    ? buildExampleBody(paramInfo.requestBody.properties)
    : null;

  // exampleCommand: --option value ... 'body-json'
  const optionsPart = paramInfo.params
    .filter((p) => p.required)
    .map(
      (p) =>
        `--${p.name} ${p.example !== undefined ? String(p.example) : `<${p.type}>`}`,
    )
    .join(" ");
  const bodyPart =
    minimalArgs && Object.keys(minimalArgs).length > 0
      ? ` '${JSON.stringify(minimalArgs)}'`
      : "";
  const exampleCommand = `acomo ${operationId}${optionsPart ? " " + optionsPart : ""}${bodyPart}`;

  const hasBody = !!paramInfo.requestBody;
  const hasParams = paramInfo.params.length > 0;
  const usageParts = [`acomo ${operationId}`];
  if (hasParams) usageParts.push("[--option value...]");
  if (hasBody) usageParts.push("[body]");

  const positionals = hasBody
    ? [
        {
          name: "body",
          type: "object",
          required: false,
          description: "リクエストボディ JSON" as string,
        },
      ]
    : [];

  return buildCommandHelp({
    command: operationId,
    kind: "api-command",
    summary,
    usage: usageParts.join(" "),
    positionals,
    options: [...paramOptions, ...GLOBAL_OPTIONS],
    inputSchema: {
      pathParams,
      queryParams,
      requestBody,
      stdinSupported: true,
      positionalJsonSupported: true,
    },
    minimalArgs,
    exampleCommand,
  });
}

/**
 * エラーハンドリング
 */
async function handleError(error: any): Promise<never> {
  const jsonMode = !process.stdout.isTTY;

  if (error.name === "ResponseError") {
    const status: number = error.response?.status || 0;
    const exitCode =
      status === 401 || status === 403
        ? EXIT_CODES.AUTH
        : status === 404
          ? EXIT_CODES.NOT_FOUND
          : EXIT_CODES.GENERAL;

    if (error.response) {
      let body: any;
      try {
        body = await error.response.json();
      } catch {
        body = { message: error.message };
      }
      if (jsonMode) {
        const errObj = {
          error: true,
          code:
            status === 401
              ? "AUTH_REQUIRED"
              : status === 403
                ? "FORBIDDEN"
                : status === 404
                  ? "NOT_FOUND"
                  : "API_ERROR",
          status,
          message: body.message ?? error.message,
          ...(status === 401
            ? { suggestion: "`acomo login` を実行してください。" }
            : {}),
        };
        console.error(JSON.stringify(errObj));
      } else {
        console.error(`API エラー (${status})`);
        console.error(
          typeof body === "string" ? body : JSON.stringify(body, null, 2),
        );
        if (status === 401) {
          console.error("\n`acomo login` を実行してください。");
        }
      }
      process.exit(exitCode);
    }
  }

  if (error.name === "FetchError") {
    const message =
      "接続に失敗しました。baseUrl（ACOMO_BASE_URL）とネットワークを確認してください。";
    if (jsonMode) {
      console.error(
        JSON.stringify({
          error: true,
          code: "CONNECTION_ERROR",
          message,
          suggestion:
            "`acomo login` で baseUrl を設定するか、環境変数 ACOMO_BASE_URL を設定してください。",
        }),
      );
    } else {
      console.error(message);
      console.error(
        "\n`acomo login` で baseUrl を設定するか、環境変数 ACOMO_BASE_URL を設定してください。",
      );
    }
    process.exit(EXIT_CODES.GENERAL);
  }

  if (error.name === "RequiredError") {
    if (jsonMode) {
      console.error(
        JSON.stringify({
          error: true,
          code: "VALIDATION_ERROR",
          message: error.message,
          suggestion:
            "必須パラメータを確認してください。`acomo <command> --help` でパラメータ情報を確認できます。",
        }),
      );
    } else {
      console.error(`必須パラメータが不足しています: ${error.message}`);
    }
    process.exit(EXIT_CODES.VALIDATION);
  }

  // 無効な JSON エラー
  if (error.message?.startsWith("無効な JSON")) {
    if (jsonMode) {
      console.error(
        JSON.stringify({
          error: true,
          code: "INVALID_INPUT",
          message: error.message,
        }),
      );
    } else {
      console.error(`エラー: ${error.message}`);
    }
    process.exit(EXIT_CODES.VALIDATION);
  }

  // その他のエラー
  if (jsonMode) {
    console.error(
      JSON.stringify({
        error: true,
        code: "UNKNOWN_ERROR",
        message: error.message,
      }),
    );
  } else {
    console.error(`エラー: ${error.message}`);
  }
  process.exit(EXIT_CODES.GENERAL);
}

/**
 * メイン処理
 */
async function main() {
  const { help, command } = getHelpRequestedCommand(process.argv);
  if (help) {
    let descriptor: HelpDescriptor;
    if (!command) {
      descriptor = buildRootHelpDescriptor();
    } else {
      const builtin = buildBuiltinHelpDescriptor(command);
      if (builtin) {
        descriptor = builtin;
      } else {
        const api = buildApiCommandHelpDescriptor(command);
        if (api) descriptor = api;
        else descriptor = buildRootHelpDescriptor(); // 未知コマンド時は root を返す
      }
    }
    console.log(JSON.stringify(descriptor, null, 2));
    process.exit(0);
  }

  const pkg = JSON.parse(
    readFileSync(resolve(scriptDir, "../package.json"), "utf-8"),
  );

  const argv = yargs(hideBin(process.argv))
    .scriptName("acomo")
    .usage("Usage: $0 <command> [options]")
    .help()
    .alias("h", "help")
    .version(pkg.version)
    .alias("v", "version")
    .fail((msg, _err, yargs) => {
      if (msg === "コマンドを指定してください。") {
        yargs.showHelp();
        process.exit(0);
      }
      const jsonMode = !process.stdout.isTTY;
      if (jsonMode) {
        console.error(
          JSON.stringify({
            error: true,
            code: "VALIDATION_ERROR",
            message: msg,
            suggestion:
              "必須パラメータを確認してください。`acomo <command> --help` でパラメータ情報を確認できます。",
          }),
        );
      } else {
        console.error(`必須パラメータが不足しています: ${msg}`);
        console.error(
          "`acomo <command> --help` でパラメータ情報を確認できます。",
        );
      }
      process.exit(EXIT_CODES.VALIDATION);
    });

  // 手書きコマンドを登録
  argv.command(
    loginCommand.command(),
    loginCommand.describe(),
    loginCommand.builder,
    loginCommand.handler,
  );

  argv.command(
    logoutCommand.command(),
    logoutCommand.describe(),
    logoutCommand.builder,
    logoutCommand.handler,
  );

  argv.command(
    configCommand.command(),
    configCommand.describe(),
    configCommand.builder,
    configCommand.handler,
  );

  argv.command(
    schemaCommand.command(),
    schemaCommand.describe(),
    schemaCommand.builder,
    schemaCommand.handler,
  );

  // 動的コマンドを登録（認証が必要なコマンド）
  const config = resolveConfig();
  if (config) {
    try {
      const configuration = createConfiguration();
      const registry = buildOperationRegistry(configuration);

      for (const [operationId] of registry) {
        const paramInfo = getOperationRequestSchema(operationId);
        const hasBody = !!paramInfo?.requestBody;

        argv.command(
          hasBody ? `${operationId} [body]` : operationId,
          operationDescriptions[operationId] ?? operationId,
          (y) => {
            // path/query params を named options として登録
            if (paramInfo) {
              for (const p of paramInfo.params) {
                const yargsType =
                  p.type === "number" || p.type === "integer"
                    ? "number"
                    : p.type === "boolean"
                      ? "boolean"
                      : "string";
                y.option(p.name, {
                  type: yargsType as "string" | "number" | "boolean",
                  description: p.description,
                  demandOption: p.required,
                });
              }
            }
            if (hasBody) {
              y.positional("body", {
                type: "string",
                description: "リクエストボディ JSON",
              });
            }
            return y;
          },
          async (cmdArgv) => {
            try {
              // ボディ JSON を位置引数から解決
              const body = await resolveBody(
                cmdArgv.body as string | undefined,
              );
              // path/query params を named options から収集
              const namedParams: Record<string, unknown> = {};
              if (paramInfo) {
                for (const p of paramInfo.params) {
                  const val = (cmdArgv as Record<string, unknown>)[p.name];
                  if (val !== undefined) namedParams[p.name] = val;
                }
              }
              const clientParams = buildClientParams(
                operationId,
                namedParams,
                body,
              );
              const entry = registry.get(operationId)!;
              const apiConfig = createConfiguration();
              const instance = new (entry.instance.constructor as any)(
                apiConfig,
              );
              const rawResponse = await (instance as any)[entry.method + "Raw"](
                clientParams,
              );
              if (!rawResponse.raw.ok) {
                throw new ResponseError(
                  rawResponse.raw,
                  "Response returned an error code",
                );
              }
              const text = await rawResponse.raw.text();
              process.stdout.write(text + "\n");
            } catch (error) {
              await handleError(error);
            }
          },
        );
      }
    } catch (error) {
      // 認証されていない場合はスキップ（login コマンドのみ利用可能）
    }
  }

  argv.demandCommand(1, "コマンドを指定してください。").strict().parse();
}

main().catch(async (error) => {
  await handleError(error);
});
